import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE } from '../config';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import Modal from './Modal';
import Icon from './Icon';

interface HappyHour {
  id: string;
  name: string | null;
  startAt: string;
  endAt: string;
  bonusPercent: number;
  status: 'upcoming' | 'active';
  imageUrl: string | null;
}

const SHOWN_KEY = 'aga_happyhour_shown_id';

function readShownId(): string | null {
  try {
    return localStorage.getItem(SHOWN_KEY);
  } catch {
    return null;
  }
}

function recordShown(id: string): void {
  try {
    localStorage.setItem(SHOWN_KEY, id);
  } catch {
    /* best-effort — a failed write just means the popup may reshow */
  }
}

export default function HappyHourPopup() {
  const { colors } = useTheme();
  const { happyHourUpdateCount } = useSocket();
  const navigate = useNavigate();
  const [happyHour, setHappyHour] = useState<HappyHour | null>(null);
  const [visible, setVisible] = useState(false);

  const check = useCallback(async () => {
    try {
      const res = await axios.get<HappyHour | null>(`${API_BASE}/happy-hours/current`);
      const hh = res.data ?? null;
      if (!hh || hh.status !== 'active') return;
      if (readShownId() === hh.id) return;
      setHappyHour(hh);
      setVisible(true);
      recordShown(hh.id);
    } catch {
      /* public best-effort */
    }
  }, []);

  // On mount + whenever the server broadcasts a happy-hour update.
  useEffect(() => { check(); }, [check, happyHourUpdateCount]);

  const close = () => setVisible(false);

  const onTopUp = () => {
    close();
    navigate('/topup');
  };

  if (!happyHour) return null;

  return (
    <Modal open={visible} onClose={close} card={false} backdropOpacity={0.78}>
      <div style={{
        position: 'relative',
        width: '100%', maxWidth: 400, margin: '0 auto',
        maxHeight: 'calc(100dvh - 48px)',
        borderRadius: 22,
        border: `1px solid ${colors.cardBorder}`,
        backgroundColor: colors.card,
        overflow: 'hidden',
        paddingBottom: 16,
        display: 'flex', flexDirection: 'column',
        animation: 'aga-modal-in 200ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}>
        <button
          aria-label="Close"
          onClick={close}
          style={{
            position: 'absolute', top: 10, right: 10, zIndex: 2,
            width: 32, height: 32, borderRadius: 16,
            backgroundColor: 'rgba(2,6,23,0.45)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <Icon name="times" size={18} color="#fff" />
        </button>

        {happyHour.imageUrl && (
          <img
            src={happyHour.imageUrl}
            alt=""
            style={{ width: '100%', height: 180, objectFit: 'cover', backgroundColor: colors.surfaceMuted }}
          />
        )}

        <div style={{ padding: '22px 22px 0', textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 28, margin: '0 auto 14px',
            backgroundColor: colors.accentSoft, border: `1px solid ${colors.accent}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon name="bolt" size={24} color={colors.accentText} />
          </div>
          <div style={{ fontSize: 22, fontWeight: 900, color: colors.text, marginBottom: 8 }}>
            {happyHour.name ? `${happyHour.name} is ON` : 'Happy Hour is ON'}
          </div>
          <div style={{ fontSize: 15, lineHeight: 1.5, color: colors.textMuted }}>
            {happyHour.bonusPercent}% bonus on your top-ups
          </div>
        </div>

        <button
          onClick={onTopUp}
          style={{
            margin: '20px 22px 0',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: '15px 0', borderRadius: 999, minHeight: 50,
            backgroundColor: colors.accentDark, color: '#020617',
            border: 'none', cursor: 'pointer',
            fontSize: 15, fontWeight: 800, fontFamily: 'inherit',
          }}
        >
          <Icon name="bolt" size={15} color="#020617" />
          Top up now
        </button>

        <button
          onClick={close}
          style={{
            margin: '10px auto 0',
            padding: '8px 12px', minHeight: 44,
            background: 'transparent', border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 700, color: colors.textMuted, fontFamily: 'inherit',
          }}
        >
          Close
        </button>
      </div>
    </Modal>
  );
}
